import { FC, useEffect, useState } from 'react'
import { KTIcon } from '../../../../../../_metronic/helpers'
import { Button } from 'react-bootstrap'
import { useTheme } from '../../../../../../_metronic/partials/layout/app-theme/ThemeContext'
import Table from '../../common/Table'
import TableHeader from '../../common/TableHeader'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../../../../redux/store'
import { deleteDealerSchemeApi, getDealerSchemeListApi } from '../../../../../redux/slices/DealerSchemeSlice'
import { toast } from 'react-toastify'

const DealerSchemeTable: FC = () => {
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchKey, setSearchKey] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedRow, setSelectedRow] = useState([])
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const { theme } = useTheme()

  // Handle nested API response structure: response.data.data.data
  const dealerSchemeSelectorData = useSelector((state: RootState) => {
    const listData = state.dealerScheme.DealerSchemeList
    if (!listData) return []
    // Handle nested structure: listData.data.data or listData.data
    const dataArray = listData.data?.data || listData.data || []
    return Array.isArray(dataArray) ? dataArray : []
  })
  
  const dealerSchemeSelectorTotal = useSelector((state: RootState) => {
    const listData = state.dealerScheme.DealerSchemeList
    if (!listData) return 0
    // Try to get total from different possible locations
    return listData.data?.total || listData.total || listData.data?.data?.length || 0
  })

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchKey)
    }, 1000)
    return () => clearTimeout(debounceTimeout)
  }, [searchKey])

  const fetchDealerSchemeListGetApi = () => {
    setLoading(true)
    dispatch(
      getDealerSchemeListApi({
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterkey: debouncedSearchQuery,
      })
    )
      .then((response: any) => {
        console.log('Dealer Scheme API Response:', response) // Debug log
        console.log('Dealer Scheme Data:', response.payload) // Debug log
        setLoading(false)
      })
      .catch((error: any) => {
        console.error('Dealer Scheme API Error:', error) // Debug log
        setLoading(false)
        toast.error('Failed to fetch dealer scheme data')
      })
  }

  useEffect(() => {
    fetchDealerSchemeListGetApi()
  }, [currentPage, rowsPerPage, debouncedSearchQuery])

  // Debug: Log data when it changes
  useEffect(() => {
    console.log('Dealer Scheme Selector Data:', dealerSchemeSelectorData)
    console.log('Dealer Scheme Selector Total:', dealerSchemeSelectorTotal)
  }, [dealerSchemeSelectorData, dealerSchemeSelectorTotal])

  const handleEdit = (id: any) => {
    navigate(`edit/${id}`)
  }

  const handleView = async (id: any) => {
    try {
      const response: any = await dispatch(
        getDealerSchemeListApi({
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          filterkey: '',
        })
      )

      const selectedDealerScheme = response.payload?.data?.data?.find(
        (item: any) => item.id === id
      )
      const dealer_scheme_drive_link = selectedDealerScheme?.dealer_scheme_drive_link

      if (dealer_scheme_drive_link) {
        window.open(dealer_scheme_drive_link, '_blank')
      } else {
        toast.error('Drive link not found')
      }
    } catch (error) {
      toast.error('Error fetching dealer scheme data')
    }
  }

  const handleDelete = (id: any) => {
    dispatch(deleteDealerSchemeApi(id)).then((response: any) => {
      if (response.payload.success) {
        toast.success(response.payload.reason)
        fetchDealerSchemeListGetApi()
      } else {
        toast.error(response.payload.reason || 'Something went wrong')
      }
    })
  }

  const handleSearchChange = (searchQuery: string) => {
    setSearchKey(searchQuery)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setCurrentPage(page)
    setRowsPerPage(newPerPage)
  }

  const columnData = [
    { name: 'Name', selector: (row: any) => row?.name, sortable: true },
    {
      name: 'Status',
      cell: (row: any) => (row?.is_active === 1 ? 'Active' : 'Inactive'),
      sortable: true,
    },
    {
      name: 'Created At',
      selector: (row: any) => new Date(row?.created_at).toLocaleDateString(),
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleView(row?.id)}
          >
            <KTIcon iconName='eye' className='fs-3' />
          </div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleEdit(row?.id)}
          >
            <KTIcon iconName='pencil' className='fs-3' />
          </div>
          <div
            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            onClick={() => handleDelete(row?.id)}
          >
            <KTIcon iconName='trash' className='fs-3' />
          </div>
        </div>
      ),
      sortable: false,
      style: { textAlign: 'left' },
    },
  ]

  return (
    <div className='card'>
      <TableHeader
        search={searchKey}
        addButtonText={'Add Dealer Scheme'}
        handleSearchChange={handleSearchChange}
        handleAddButton={() => navigate('add')}
        showFilter={true}
        filterContent={
          <div>
            <p>Custom filter form</p>
            <div className='d-flex justify-content-end'>
              <Button variant='secondary' className='me-2'>
                Close
              </Button>
              <Button variant={`${theme}`}>Apply Filters</Button>
            </div>
          </div>
        }
      />

      <div className='card-body p-0 shadow-sm'>
        <Table
          data={dealerSchemeSelectorData}
          columns={columnData}
          isLoading={loading}
          handlePerRowsChange={handlePerRowsChange}
          handlePageChange={handlePageChange}
          totalRows={dealerSchemeSelectorTotal}
          showPagination={true}
          onRowClicked={(row) => {}}
          selectableRows
          onSelectedRowsChange={(selected) => {
            setSelectedRow(selected.selectedRows)
          }}
        />
      </div>
    </div>
  )
}

export default DealerSchemeTable

